import { useSeason } from "../../context/SeasonContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getCasePriority, setCasePriority } from "../../api/caseShowcase";
import styles from "./CaseModal.module.css";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import { SeasonLink } from "../Seasons/SeasonLink";
import { caseTitle } from "../../utils/caseTitle";
import { casePriorityAccess } from "../../utils/casePriorityAccess";
import { CaseArtwork } from "../CaseArtwork/CaseArtwork.jsx";
import { getCaseArtwork } from "../CaseArtwork/caseArtwork.js";

const URL_RE = /(https?:\/\/[^\s,;]+)/gi;

function extractLinks(text) {
  if (!text) return [];
  const matches = String(text).match(URL_RE) || [];
  return Array.from(
    new Set(matches.map((v) => String(v).trim()).filter(Boolean)),
  );
}

function stripLinks(text) {
  if (!text) return "";
  return String(text).replace(URL_RE, "").trim();
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
}

export function CaseModal({ caseItem, isOpen, onClose, onCaseChanged }) {
  const { isAuthenticated } = useAuth();
  const { canEdit, season, archiveEdit } = useSeason();
  const { canSetPriority, reason: priorityUnavailable } = casePriorityAccess({
    season,
    canEdit,
    archiveEdit,
    isAuthenticated,
  });
  const [priority, setPriority] = useState(null);
  const [priorityLoading, setPriorityLoading] = useState(false);
  const [priorityError, setPriorityError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const caseId = caseItem?.case_id;

  useEffect(() => {
    if (isOpen) {
      setPriority(null);
      setPriorityError("");
      setSuccessMessage("");
    }
  }, [isOpen, caseItem]);

  useEffect(() => {
    if (!isOpen || !isAuthenticated || !caseId) return;

    let isCancelled = false;
    const loadPriority = async () => {
      try {
        const data = await getCasePriority(caseId);
        if (!isCancelled) {
          const resolvedPriority = data?.priority ?? null;
          setPriority(resolvedPriority);
        }
      } catch {
        if (!isCancelled) {
          setPriority(null);
        }
      }
    };
    loadPriority();
    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, isOpen, caseId]);

  const handleSetPriority = async (newPriority) => {
    if (!caseId || priorityLoading || !canSetPriority) return;

    setPriorityLoading(true);
    setPriorityError("");
    setSuccessMessage("");

    try {
      await setCasePriority(caseId, newPriority);
      setPriority(newPriority);
      setSuccessMessage("Приоритет успешно обновлён!");
      onCaseChanged?.();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setPriorityError(err.message || "Не удалось установить приоритет.");
    } finally {
      setPriorityLoading(false);
    }
  };

  if (!isOpen || !caseItem) return null;

  const partnerName =
    caseItem.partner_name?.trim() === "ЮФУ.проШкольников"
      ? "Центр тестирования и подготовки к олимпиадам АПП ЮФУ"
      : caseItem.partner_name;

  const links = extractLinks(caseItem.attachments);
  const attachmentText = stripLinks(caseItem.attachments);
  const artwork = getCaseArtwork(caseItem, season);

  return (
    <ModalPortal onClose={onClose} isMobile={isMobile}>
      <div
        className={styles.modalContentWrapper}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.dragHandleWrapper}>
          <div className={styles.dragHandle} />
        </div>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderContent}>
            <h2 className={styles.modalTitle}>{caseTitle(caseItem)}</h2>
            <div className={artwork ? styles.modalPartnerRow : undefined}>
              {artwork && (
                <CaseArtwork
                  caseItem={caseItem}
                  className={styles.modalArtwork}
                />
              )}
              <p className={styles.modalPartner}>{partnerName}</p>
            </div>
          </div>

          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Закрыть"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className={styles.modalBody}>
          {priorityError ? (
            <p className={styles.error}>{priorityError}</p>
          ) : null}
          {successMessage ? (
            <p className={styles.success}>{successMessage}</p>
          ) : null}

          <div className={styles.priorityBlock}>
            {priorityUnavailable && (
              <p className={styles.priorityNotice} role="status">
                {priorityUnavailable}
              </p>
            )}
            <div className={styles.priorityGrid}>
              <div>
                <p className={styles.fieldTitleMain}>Выбор кейса</p>
                <p className={styles.fieldDescription}>
                  Укажите приоритет для этого задания. Это поможет отслеживать
                  самые важные для вас кейсы.
                </p>
                {isAuthenticated ? (
                  <>
                    <div className={styles.priorityButtons}>
                      <button
                        type="button"
                        className={`${styles.priorityBtn} ${priority === 1 ? styles.priorityBtnActive : ""}`}
                        onClick={() => handleSetPriority(1)}
                        disabled={priorityLoading || !canSetPriority}
                        aria-pressed={priority === 1}
                      >
                        Высокий
                      </button>
                      <button
                        type="button"
                        className={`${styles.priorityBtn} ${priority === 2 ? styles.priorityBtnActive : ""}`}
                        onClick={() => handleSetPriority(2)}
                        disabled={priorityLoading || !canSetPriority}
                        aria-pressed={priority === 2}
                      >
                        Средний
                      </button>
                      <button
                        type="button"
                        className={`${styles.priorityBtn} ${priority === 3 ? styles.priorityBtnActive : ""}`}
                        onClick={() => handleSetPriority(3)}
                        disabled={priorityLoading || !canSetPriority}
                        aria-pressed={priority === 3}
                      >
                        Низкий
                      </button>
                      {priority !== null ? (
                        <button
                          type="button"
                          className={styles.priorityClearBtn}
                          onClick={() => handleSetPriority(null)}
                          disabled={priorityLoading || !canSetPriority}
                        >
                          Сбросить
                        </button>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className={styles.authHint}>
                    <button
                      type="button"
                      className={styles.loginLink}
                      onClick={() => navigate("/login")}
                    >
                      Войдите
                    </button>{" "}
                    чтобы установить приоритет
                  </p>
                )}
              </div>

              {!isAuthenticated && (
                <div className={styles.applyBlock}>
                  <p className={styles.applyText}>
                    Выбрать проект могут только зарегистрированные и прошедшие
                    отбор участники
                  </p>
                  {season?.application_url ? (
                    <a
                      href={season.application_url}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.primaryBtn}
                    >
                      Подать заявку на участие в интенсиве
                    </a>
                  ) : (
                    <SeasonLink to="/my-season" className={styles.primaryBtn}>
                      Подать заявку на участие в интенсиве
                    </SeasonLink>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={styles.fieldBlockPrimary}>
            <p className={styles.fieldTitle}>Что нужно сделать</p>
            <p className={styles.fieldValueMain}>
              {caseItem.problem || "Описание задачи пока не заполнено."}
            </p>
          </div>

          <div className={styles.fieldGrid}>
            {caseItem.expected_solution ? (
              <div className={styles.fieldBlock}>
                <p className={styles.fieldTitle}>Ожидаемый результат</p>
                <p className={styles.fieldValue}>
                  {caseItem.expected_solution}
                </p>
              </div>
            ) : null}
            {caseItem.input_data ? (
              <div className={styles.fieldBlock}>
                <p className={styles.fieldTitle}>Условия и ограничения</p>
                <p className={styles.fieldValue}>{caseItem.input_data}</p>
              </div>
            ) : null}
          </div>

          {caseItem.partner_description ? (
            <div className={styles.fieldBlock}>
              <p className={styles.fieldTitle}>О партнёре</p>
              <p className={styles.fieldValue}>
                {caseItem.partner_description}
              </p>
            </div>
          ) : null}

          {links.length > 0 || attachmentText ? (
            <div className={styles.fieldBlock}>
              <p className={styles.fieldTitle}>Материалы / ссылки</p>
              {links.length > 0 && (
                <div className={styles.linkList}>
                  {links.map((url, idx) => (
                    <a
                      key={`${url}-${idx}`}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Файл {idx + 1}
                    </a>
                  ))}
                </div>
              )}
              {attachmentText ? (
                <p className={styles.fieldValue}>{attachmentText}</p>
              ) : null}
            </div>
          ) : null}

          <div className={styles.caseMeta}>
            {caseItem.track ? (
              <span className={styles.tag}>{caseItem.track}</span>
            ) : null}
            {caseItem.location ? (
              <span className={styles.tag}>{caseItem.location}</span>
            ) : null}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button type="button" className={styles.ghostBtn} onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}
